// useOrgSubscriptionState — single source of truth for trial/billing
// status of an organization. Drives Phase 3 trial banner + workspace
// lockout overlay. Backed by RPC `get_org_subscription_state` which
// applies the same rule everywhere ("locked" = trial expired or
// status in past_due/canceled/incomplete).
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type OrgSubscriptionState = {
  has_subscription: boolean;
  status: "trialing" | "active" | "past_due" | "canceled" | "incomplete" | "none";
  product?: string | null;
  billing_cycle?: "monthly" | "annual" | null;
  trial_ends_at?: string | null;
  current_period_end?: string | null;
  cancel_at_period_end?: boolean | null;
  locked_at?: string | null;
  is_trialing: boolean;
  is_locked: boolean;
  trial_days_remaining: number | null;
  is_org_super_admin: boolean;
  is_management_account: boolean;
};

export function useOrgSubscriptionState(orgId: string | null | undefined) {
  return useQuery<OrgSubscriptionState | null>({
    queryKey: ["org-subscription-state", orgId],
    enabled: Boolean(orgId),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      if (!orgId) return null;
      const { data, error } = await supabase.rpc(
        "get_org_subscription_state",
        { _org_id: orgId },
      );
      if (error) throw error;
      const obj = (data ?? {}) as Record<string, unknown>;
      if (typeof obj.error === "string") return null;
      return {
        has_subscription:    Boolean(obj.has_subscription),
        status:              (obj.status ?? "none") as OrgSubscriptionState["status"],
        product:             (obj.product as string | null) ?? null,
        billing_cycle:       (obj.billing_cycle as "monthly" | "annual" | null) ?? null,
        trial_ends_at:       (obj.trial_ends_at as string | null) ?? null,
        current_period_end:  (obj.current_period_end as string | null) ?? null,
        cancel_at_period_end:Boolean(obj.cancel_at_period_end),
        locked_at:           (obj.locked_at as string | null) ?? null,
        is_trialing:         Boolean(obj.is_trialing),
        is_locked:           Boolean(obj.is_locked),
        trial_days_remaining:(obj.trial_days_remaining as number | null) ?? null,
        is_org_super_admin:  Boolean(obj.is_org_super_admin),
        is_management_account: Boolean(obj.is_management_account),
      };
    },
  });
}
